/**
 * Admin component for managing Telegram users
 */
import { useState } from 'react';
import { Plus, Search, AlertCircle, Loader2 } from 'lucide-react';
import { useTelegramUsersList } from '../hooks/useAdminUsers';
import { AddUserForm } from './AddUserForm';
import { UsersList } from './UsersList';
import { haptic } from '@/shared/utils/telegram';

export function UserManagement() {
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const { data: users, isLoading, error } = useTelegramUsersList();

  const handleAddClick = () => {
    haptic.impact('light');
    setShowAddForm(!showAddForm);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  // Filter users based on search query
  const filteredUsers =
    users?.filter((user) => {
      const query = searchQuery.toLowerCase();
      return (
        user.telegramUserId.toLowerCase().includes(query) ||
        user.username?.toLowerCase().includes(query) ||
        user.firstName?.toLowerCase().includes(query) ||
        user.lastName?.toLowerCase().includes(query)
      );
    }) || [];

  // Show error state
  if (error) {
    return (
      <div className="bg-rose-500/10 border border-rose-500/20 rounded-2xl p-5 flex flex-col items-center gap-3">
        <AlertCircle size={32} className="text-rose-600" />
        <div className="text-center">
          <p className="text-sm font-bold text-rose-600">Failed to load users</p>
          <p className="text-xs text-rose-500/80 mt-1">
            {error instanceof Error ? error.message : 'Please try again later'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Add User Button */}
      <button
        onClick={handleAddClick}
        className="w-full py-3 px-4 bg-[var(--tg-theme-link-color)]/10 text-[var(--tg-theme-link-color)] rounded-xl text-sm font-bold uppercase tracking-wide hover:bg-[var(--tg-theme-link-color)]/20 transition-colors flex items-center justify-center gap-2"
      >
        <Plus size={18} strokeWidth={3} />
        Add User
      </button>

      {/* Add User Form */}
      {showAddForm && (
        <div className="animate-in slide-in-from-top duration-200">
          <AddUserForm onSuccess={() => setShowAddForm(false)} />
        </div>
      )}

      {/* Search Box */}
      <div className="relative">
        <input
          type="text"
          placeholder="Search by ID or username..."
          value={searchQuery}
          onChange={handleSearchChange}
          className="w-full h-11 pl-10 pr-4 bg-[var(--tg-theme-secondary-bg-color)] rounded-xl text-[13px] font-medium placeholder:text-[var(--tg-theme-hint-color)] focus:outline-none focus:ring-2 focus:ring-[var(--tg-theme-link-color)]/20"
        />
        <Search
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--tg-theme-hint-color)]"
        />
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-3">
            <Loader2 size={32} className="animate-spin text-[var(--tg-theme-link-color)]" />
            <p className="text-xs font-bold text-[var(--tg-theme-hint-color)] uppercase">
              Loading users...
            </p>
          </div>
        </div>
      )}

      {/* Users List */}
      {!isLoading && <UsersList users={filteredUsers} />}

      {/* Empty State */}
      {!isLoading && filteredUsers.length === 0 && users && users.length > 0 && (
        <div className="text-center py-12">
          <p className="text-sm font-medium text-[var(--tg-theme-hint-color)]">
            No users found
          </p>
        </div>
      )}

      {/* Stats */}
      {!isLoading && users && users.length > 0 && (
        <div className="text-xs text-[var(--tg-theme-hint-color)] text-center pt-2">
          Showing {filteredUsers.length} of {users.length} users
        </div>
      )}
    </div>
  );
}
